import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowUpRight, Home } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { HomeMap } from "@/components/map/HomeMap";
import { milesBetween } from "@/components/map/geo";
import { BaseGlyph, baseCamera, coords } from "@/components/bases/baseUtils";
import { ListingCard } from "@/components/listings/ListingCard";
import { MarketSnapshot } from "@/components/listings/MarketSnapshot";
import { AlertsBand } from "@/components/forms/AlertsBand";
import { PcsCta } from "@/components/home/Sections";
import { Container, PillButton } from "@/components/common/ui";
import { useBases, useListings } from "@/hooks/useData";
import { miles, money } from "@/lib/format";

const RADII = [5, 10, 15, 20, 30];

const Neighborhoods = ({ listings }) => {
  const rows = Object.values(listings.reduce((acc, l) => {
    const k = `${l.neighborhood}|${l.city}`;
    acc[k] = acc[k] || { name: l.neighborhood, city: l.city, n: 0, closest: Infinity, low: Infinity };
    acc[k].n += 1;
    acc[k].closest = Math.min(acc[k].closest, l.base_miles);
    acc[k].low = Math.min(acc[k].low, l.price);
    return acc;
  }, {})).sort((a, b) => a.closest - b.closest);
  if (!rows.length) return null;
  return (
    <Table data-testid="neighborhood-table">
      <TableHeader><TableRow><TableHead>Neighborhood</TableHead><TableHead>City</TableHead><TableHead className="text-right">Closest home</TableHead><TableHead className="text-right">From</TableHead></TableRow></TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={`${r.name}-${r.city}`}>
            <TableCell className="font-medium">{r.name}</TableCell><TableCell>{r.city}</TableCell>
            <TableCell className="text-right font-mono text-[13px]">{miles(r.closest)}</TableCell><TableCell className="text-right">{money(r.low)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default function BaseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const bases = useBases();
  const [radius, setRadius] = useState(10);
  const [orbit, setOrbit] = useState(true);
  const params = useMemo(() => ({ base: id, radius, sort: "distance" }), [id, radius]);
  const { data, loading } = useListings(params);
  const base = bases.find((b) => b.id === id);
  const focus = useMemo(() => {
    if (!base) return null;
    const phone = window.innerWidth < 640;
    return { ...baseCamera(base, 1), padding: phone ? { top: 80, bottom: 380, left: 0, right: 0 } : { top: 0, bottom: 0, left: 520, right: 0 } };
  }, [base]);
  const listings = data?.listings || [];
  const nearby = base ? bases.filter((b) => b.id !== id).map((b) => ({ ...b, d: milesBetween(base, b) })).sort((a, b) => a.d - b.d).slice(0, 4) : [];

  if (bases.length && !base) {
    return <Container className="py-32 text-center"><p className="text-2xl" data-testid="base-not-found">We couldn't find that installation.</p><Link to="/bases" className="mt-4 inline-block underline">See all bases</Link></Container>;
  }

  return (
    <div data-testid="base-detail-page">
      <section className="relative h-[86svh] min-h-[600px] bg-neutral-900">
        {base && (
          <HomeMap key={base.id} testId="base-detail-map" listings={listings} bases={bases} selectedBaseId={base.id} radius={radius}
            onBaseClick={(bid) => navigate(`/bases/${bid}`)} onListingClick={(k) => navigate(`/listing/${k}`)}
            initialView={{ center: [base.lng, base.lat], zoom: 12, pitch: 50, bearing: 0 }} focus={focus} orbit={orbit} onOrbitChange={setOrbit} vignette controlsClass="top-20" />
        )}
        {base && (
          <div className="pointer-events-none absolute inset-x-3 bottom-3 z-10 sm:inset-x-auto sm:bottom-8 sm:left-8 sm:max-w-[520px]">
            <div className="pointer-events-auto animate-rise-in rounded-md bg-white p-6 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6)] sm:p-8" data-testid="base-detail-card">
              <div className="flex items-center gap-3"><BaseGlyph base={base} selected /><p className="eyebrow">{base.branch} · {base.city}, VA</p></div>
              <h1 className="mt-4 font-display text-4xl font-normal leading-[1.05] text-ink sm:text-5xl" data-testid="base-detail-name">{base.name}</h1>
              <p className="mt-3 text-[16px] leading-relaxed text-neutral-600">{base.summary}</p>
              <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-1 border-t border-neutral-200 pt-4">
                <p className="font-mono text-[11px] text-neutral-500">{coords(base)}</p>
                <p className="flex items-center gap-1.5 text-[14px]" data-testid="base-detail-count"><Home className="h-4 w-4 text-brass-deep" /><strong className="font-medium">{loading ? "…" : listings.length}</strong> homes within {radius} mi</p>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <PillButton href="#homes" testId="base-detail-see-homes">See the homes</PillButton>
                <PillButton href="#pcs-form" tone="outline" arrow={false} testId="base-detail-pcs">Start my PCS plan</PillButton>
              </div>
            </div>
          </div>
        )}
      </section>

      <section id="homes" className="scroll-mt-20 py-14 sm:py-20">
        <Container>
          <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="eyebrow">Homes near {base?.short}</p>
              <h2 className="mt-2 text-3xl font-normal sm:text-4xl">{loading ? "Finding homes…" : `${listings.length} homes within ${radius} miles`}</h2>
              <p className="mt-1 text-[15px] text-neutral-600">Sorted by straight-line distance to the installation{data?.sample ? " · sample listings until the REIN feed is live" : ""}.</p>
            </div>
            <div className="flex gap-1 rounded-full bg-neutral-100 p-1" data-testid="radius-chips">
              {RADII.map((r) => (
                <button key={r} onClick={() => setRadius(r)} data-testid={`radius-chip-${r}`}
                  className={`rounded-full px-4 py-1.5 text-[13px] transition-colors ${radius === r ? "bg-ink text-white" : "text-neutral-600 hover:text-ink"}`}>{r} mi</button>
              ))}
            </div>
          </div>
          <div className="mt-8"><MarketSnapshot listings={listings} title={`Market snapshot · ${radius} mi around ${base?.short || "base"}`} sample={data?.sample} /></div>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {listings.map((l) => <ListingCard key={l.listing_key} listing={l} baseShort={base?.short} />)}
          </div>
          {!loading && !listings.length && <p className="mt-8 text-neutral-600" data-testid="base-no-homes">No homes inside {radius} miles yet — try a wider radius.</p>}
          <div className="mt-8"><PillButton to={`/search?base=${id}&radius=${radius}&sort=distance`} testId="base-detail-open-search">Open in map search</PillButton></div>

          <div className="mt-16 grid gap-10 lg:grid-cols-[1.4fr_1fr]">
            <div>
              <h3 className="text-2xl font-normal">Neighborhoods around {base?.short}</h3>
              <p className="mb-4 mt-1 text-[14px] text-neutral-500">Based on the homes currently shown.</p>
              <div className="rounded-md border border-neutral-200"><Neighborhoods listings={listings} /></div>
            </div>
            <div>
              <h3 className="text-2xl font-normal">Nearby installations</h3>
              <ul className="mt-5 space-y-2">
                {nearby.map((b) => (
                  <li key={b.id}>
                    <Link to={`/bases/${b.id}`} data-testid={`nearby-base-${b.id}`} className="group flex items-center gap-3 rounded-md border border-neutral-200 p-3 hover:border-ink">
                      <BaseGlyph base={b} size="h-8 w-8" />
                      <span className="flex-1"><span className="block text-[15px] font-medium">{b.short}</span><span className="text-[12px] text-neutral-500">{b.branch} · {b.city}</span></span>
                      <span className="font-mono text-[12px] text-neutral-600">{miles(b.d)}</span>
                      <ArrowUpRight className="h-4 w-4 text-neutral-400 group-hover:text-ink" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </section>
      <AlertsBand base={base} />
      <PcsCta defaultBaseId={id} title={base ? `Reporting to ${base.short}? Let's talk.` : undefined} />
    </div>
  );
}
